<template>

  <div class="bot-container-wrapper"> 

    <div class="modals">

      <ModalWindow @modalClickOnBackground="setImportBotModalWindow(false)" :modalOpen="modalsState.importOpen" class="import-bot">
        <div class="title">Import Bot Data</div>

        <div class="text">
          Please, paste bot data as JSON string in text field below and click <span class="highlight">"Import"</span>
        </div>

        <div class="textarea">
          <BasicTextarea v-model="importBot.botDataString" placeholder="Type your new Bot Name" id="importBotTextarea" @focus="removeImportBotError"></BasicTextarea>
          <div class="error" v-if="importBot.botDataError">{{ importBot.botDataError }}</div>
        </div>
        
        <div class="actions">
          <BotActionButton class="blue shadow" @click="importBotData">Import</BotActionButton>
          <BotActionButton class="gray shadow" @click="setImportBotModalWindow(false)">Cancel</BotActionButton>
        </div>

      </ModalWindow>

      <ModalWindow @modalClickOnBackground="setExportBotModalWindow(false)" :modalOpen="modalsState.exportOpen" class="export-bot">
        <div class="title">Export Bot Data</div>

        <div class="text" v-show="isBotDataCopiedToClipboard">Bot Data has bin Copied to Clipboard</div>

        <div class="textarea export-bot-data">
          {{JSON.stringify(botData)}}
        </div>

        <div class="actions">
          <BotActionButton class="blue shadow" @click="copyBotDataToClipboard()">Copy to Clipboard</BotActionButton>
          <BotActionButton class="gray shadow" @click="setExportBotModalWindow(false)">Close</BotActionButton>
        </div>

      </ModalWindow>

    </div>

    <div class="bot-container">
      
      <Dialogue v-for="(dialogue, index) in botData" 
              v-bind:key="dialogue.id"
              v-bind:index="index" 
              v-bind:dialogue="dialogue"
              @deleteDialogue="deleteDialogue" 
              v-bind:id="'dialogue_'+index"
              >

    </Dialogue>

    </div>
    
  </div>

</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Dialogue from '@/components/app/botComponents/Dialogue';
import ModalWindow from "@/components/app/general/ModalWindow";
import BasicTextarea from "@/components/inputs/BasicTextarea";
import BotActionButton from "@/components/buttons/BotActionButton";
import helpers from '@/helpers';
import validators from '@/validators';

export default {
  data: () => {
    return {
      modalsState: {
        importOpen: false,
        exportOpen: false
      },
      importBot: {
        botDataString: "",
        botDataObject: {},
        botDataError: ""
      },
      botDataCopiedToClipboard: false
    }
  },
  computed: {
    ...mapGetters(['bot', 'botData']),
    isBotDataCopiedToClipboard() {
      return this.botDataCopiedToClipboard;
    }
  },
  async mounted() {
    const id = this.$route.params.id;

    await this.fetchBot(id);

    if (!this.bot){
      await this.$router.push('/404');
    } 

    this.$root.$emit('botFetchingDone');

    if (!this.checkForExport()) {
      this.checkForImport();
    }

    this.$root.$on('saveBot', (id) => {
      if (this.bot.id === id) {
        this.saveBotDataToDatabase();
      }
    });
    this.$root.$on('addDialogue', this.addDialogue)

    document.addEventListener("keydown", (e) => { // CTRL+S / CMD+S
      if ((window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey)  && e.keyCode === 83) {
        e.preventDefault()
        this.saveBotDataToDatabase()
      }
    }, false);

  },
  methods: {
    ...mapActions(['fetchBot', 'saveBotData', 'updateBotDataAction']),
    addDialogue() {
      this.bot.botData.push({
          dialogue_command: "",
          dialogue_name: "",
          id: helpers.createID(),
          dialogue_nodes: [{
            id: helpers.createID(),
            text: "",
            answers: []
          }]
        })
      this.$nextTick(() => {
        helpers.scrollElementIntoView("dialogue_" + (this.bot.botData.length - 1), {behavior: "smooth", inline: "center"});
      })
    },
    deleteDialogue(index) { this.bot.botData.splice(index, 1)},
    saveBotDataToDatabase: async function () {
      const data = { 
        id: this.bot.id,
        data: this.bot.botData
      }
      await this.saveBotData(data);
      this.$root.$emit('botSavingDone');
    },
    checkForExport() { 
      // services data in json string to console if ?export=1
      const queryString = window.location.search;
      if (queryString) {
        const urlParams = new URLSearchParams(queryString);
        const exportBot = urlParams.get('export');
        if (exportBot !== null) {
          this.setExportBotModalWindow(true);
          helpers.focusOnInputAfterModalOpen("#importBotTextarea", 400);
          return true;
        } 
      } 
      return false;
    },
    checkForImport() {
      // services data in json string to console if ?export=1
      const queryString = window.location.search;
      if (queryString) {
        const urlParams = new URLSearchParams(queryString);
        const importBot = urlParams.get('import');
        if (importBot !== null) {
          this.setImportBotModalWindow(true);
          helpers.focusOnInputAfterModalOpen("#importBotTextarea", 400);
          return true;
        }
      }
      return false;
    },
    setImportBotModalWindow(value) {
      this.modalsState.importOpen = value;
    },
    setExportBotModalWindow(value) {
      this.modalsState.exportOpen = value;
    },
    async importBotData() {
      this.importBot.botDataObject = JSON.parse(this.importBot.botDataString);
      if (validators.isBotDataValid(this.importBot.botDataObject)) {
        const data = { 
          id: this.bot.id,
          data: this.importBot.botDataObject
        }
        await this.saveBotData(data);
        this.setImportBotModalWindow(false);
      } else {
        this.importBot.botDataError = "Bot data is not valid";
      }
    },
    removeImportBotError() {
      this.importBot.botDataError = "";
    },
    copyBotDataToClipboard() {
       navigator.clipboard.writeText(JSON.stringify(this.botData)).then(() => {
         this.botDataCopiedToClipboard = true;
         setTimeout(function () {
           this.botDataCopiedToClipboard = false;
         }, 2000);
       });
    }
  },
  components: {Dialogue, ModalWindow, BasicTextarea, BotActionButton}
}
</script>

<style lang="scss" scoped>
  .bot-container-wrapper {
    width: calc(100% - 320px);
    height: calc(100vh - 60px);
    overflow: scroll;
    background-color: #131415;
    
    .bot-container {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items:baseline;
      width: auto;
      height: auto;
      padding: 20px;
    }
  } 

  .add-dialogue {
    width: 64px;
    height: 64px;
    font-size: 3em;
    padding: 8px 0 0 0;
    text-align: center;
    background-color: rgba($color: #fff, $alpha: 0.25);
    margin: 10px;
    border-radius: 4px;
    transition: all 0.3s cubic-bezier(.25,.8,.25,1);
    color: #888;
    cursor: pointer;
    
    &:hover {
      background-color: rgba($color: #fff, $alpha: 0.5);
      color: #eee;
    }
  }
  .modals {
    .import-bot, .export-bot {
      .title {
        margin: 36px auto 6px;
      }
      .text {
        margin: 6px auto;
      }
      .textarea {
        margin: 12px auto;
        width: 90%;
      }
      .actions {
        margin: 12px auto 36px;
      }
    }
    .export-bot {
      .export-bot-data {
        font-size: 9px;
        border: 1px solid #ccc;
        border-radius: 4px;
        padding: 8px 10px;
        color: #333;
        background-color: #eee;
        overflow-y: scroll;
        max-height: 300px;
      }
    }
  }
</style>